import React, { useEffect, useState } from 'react';
import { Search, MessageSquareText, Camera, Video, HelpCircle, StickyNote, Trash2, ExternalLink, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'responded', label: 'Responded' },
  { key: 'closed', label: 'Closed' },
];

const TYPE_META = {
  photos: { label: 'Real Photos', icon: Camera, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  video: { label: 'Product Video', icon: Video, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  question: { label: 'Question', icon: HelpCircle, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  note: { label: 'Note', icon: StickyNote, color: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const STATUS_META = {
  new: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  responded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function AdminInquiries() {
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusDraft, setStatusDraft] = useState('new');
  const [noteDraft, setNoteDraft] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [replyChannel, setReplyChannel] = useState('internal');

  const load = () => {
    const params = { limit: 100 };
    if (status !== 'all') params.status = status;
    if (q.trim()) params.q = q.trim();
    api.get('/admin/inquiries', { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [status, q]);

  const openDetail = (inq) => {
    setSelected(inq);
    setStatusDraft(inq.status || 'new');
    setNoteDraft(inq.admin_note || '');
  };

  const saveStatus = async () => {
    if (!selected) return;
    try {
      const { data: fresh } = await api.put(`/admin/inquiries/${selected.id}/status`, { status: statusDraft, admin_note: noteDraft });
      toast.success('Inquiry updated');
      setSelected(fresh);
      load();
    } catch { toast.error('Update failed'); }
  };

  const sendReply = async () => {
    if (!selected) return;
    const text = replyDraft.trim();
    if (!text) { toast.error('Reply cannot be empty'); return; }
    try {
      const { data: fresh } = await api.post(`/admin/inquiries/${selected.id}/reply`, {
        message: text, channel: replyChannel, set_status: 'responded',
      });
      toast.success('Reply saved');
      setSelected(fresh);
      setReplyDraft('');
      setStatusDraft(fresh.status);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reply failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try { await api.delete(`/admin/inquiries/${id}`); toast.success('Deleted'); setSelected(null); load(); }
    catch { toast.error('Delete failed'); }
  };

  const counts = data?.counts || {};

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Inquiries</h1>
          <p className="text-sm text-muted-foreground">Customer requests for real photos, videos, product questions and checkout notes.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)}
            className={`inline-flex items-center gap-1 rounded-full border border-border px-4 py-1.5 text-xs font-semibold ${status === t.key ? 'bg-[hsl(var(--primary))] text-white' : 'bg-white text-foreground'}`}
            data-testid={`inquiries-tab-${t.key}`}>
            {t.label}{t.key !== 'all' && counts[t.key] ? <span className="ml-1 rounded-full bg-black/10 px-1.5 py-0.5 text-[10px]">{counts[t.key]}</span> : null}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 rounded-full border border-border bg-white px-3">
          <Search size={14} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, product, order#" className="w-64 bg-transparent py-2 text-sm outline-none" data-testid="inquiries-search" />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-inquiries-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Type</th>
              <th className="p-3">Product / Order</th>
              <th className="p-3">Message</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((inq) => {
              const T = TYPE_META[inq.inquiry_type] || TYPE_META.question;
              const Icon = T.icon;
              return (
                <tr key={inq.id} className="border-t border-border align-top hover:bg-[hsl(var(--gaze-ivory))]/40">
                  <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(inq.created_at).toLocaleString()}<br /><span className="text-[10px] font-mono">{inq.id.slice(0, 8)}</span></td>
                  <td className="p-3 text-xs">
                    <p className="font-semibold text-foreground">{inq.name || 'Guest'}</p>
                    {inq.email && <p className="flex items-center gap-1 text-muted-foreground"><Mail size={10} /> {inq.email}</p>}
                    {inq.phone && <p className="flex items-center gap-1 text-muted-foreground"><Phone size={10} /> {inq.phone}</p>}
                  </td>
                  <td className="p-3"><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${T.color}`}><Icon size={11} /> {T.label}</span><p className="mt-1 text-[10px] uppercase text-muted-foreground">{inq.source}</p></td>
                  <td className="p-3 text-xs">
                    {inq.product ? (
                      <Link to={`/product/${inq.product.slug}`} className="inline-flex items-start gap-2">
                        <img src={inq.product.image} alt="" className="h-9 w-9 rounded-lg object-cover" />
                        <span className="flex-1"><span className="font-semibold">{inq.product.name}</span><br /><span className="text-[10px] text-muted-foreground">{inq.product.sku}</span></span>
                      </Link>
                    ) : <span className="text-muted-foreground">—</span>}
                    {inq.order_number && <p className="mt-1 text-[10px] text-muted-foreground">Order: <span className="font-mono font-semibold">{inq.order_number}</span></p>}
                  </td>
                  <td className="max-w-xs p-3"><p className="line-clamp-2 text-xs">{inq.message || <span className="italic text-muted-foreground">No message</span>}</p></td>
                  <td className="p-3"><span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_META[inq.status] || ''}`}>{(inq.status || 'new').replace('_', ' ')}</span></td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => openDetail(inq)} data-testid="inquiry-open" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] px-3 py-1 text-[11px] font-semibold text-white"><ExternalLink size={11} /> Open</button>
                      <button onClick={() => del(inq.id)} className="grid h-7 w-7 place-items-center rounded-full border border-border text-destructive"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && data.items.length === 0 && (
          <div className="p-10 text-center">
            <MessageSquareText className="mx-auto mb-2 text-muted-foreground" size={28} />
            <p className="text-sm text-muted-foreground">No inquiries in this view.</p>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="inquiry-detail-dialog">
          <DialogHeader><DialogTitle>Inquiry details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                {(() => { const T = TYPE_META[selected.inquiry_type] || TYPE_META.question; const Icon = T.icon; return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${T.color}`}><Icon size={12} /> {T.label}</span>; })()}
                <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${STATUS_META[selected.status] || ''}`}>{(selected.status || 'new').replace('_', ' ')}</span>
                <span className="text-xs text-muted-foreground">Source: {selected.source}</span>
                <span className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString()}</span>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-[hsl(var(--gaze-ivory))] p-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Customer</p>
                  <p className="mt-1 font-semibold">{selected.name || 'Guest'}</p>
                  {selected.email && <p className="text-xs text-muted-foreground">Email: {selected.email}</p>}
                  {selected.phone && <p className="text-xs text-muted-foreground">Phone: {selected.phone}</p>}
                  {selected.user_id && <p className="text-[10px] text-muted-foreground">Registered user · {selected.user_id.slice(0, 8)}</p>}
                </div>
                <div className="rounded-xl bg-[hsl(var(--gaze-ivory))] p-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Context</p>
                  {selected.product ? (
                    <div className="mt-1 flex items-center gap-2">
                      <img src={selected.product.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      <div><p className="font-semibold">{selected.product.name}</p><p className="text-xs text-muted-foreground">SKU {selected.product.sku}</p></div>
                    </div>
                  ) : <p className="mt-1 text-xs text-muted-foreground">No product attached</p>}
                  {selected.order_number && <p className="mt-2 text-xs">Order <span className="font-mono font-semibold">{selected.order_number}</span></p>}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Message</p>
                <div className="mt-1 whitespace-pre-wrap rounded-xl bg-white p-3 text-sm ring-1 ring-inset ring-border">{selected.message || <span className="italic text-muted-foreground">(none)</span>}</div>
              </div>

              {/* Conversation history */}
              {(selected.responses || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Conversation</p>
                  <div className="mt-1 space-y-2">
                    {selected.responses.map((r) => (
                      <div key={r.id} className="rounded-xl bg-[hsl(var(--primary))]/5 p-3 ring-1 ring-inset ring-[hsl(var(--primary))]/20">
                        <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
                          <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-[hsl(var(--primary))]">{r.author}</span>
                          <span>via {r.channel || 'internal'}</span>
                          <span>· {new Date(r.created_at).toLocaleString()}</span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm">{r.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply composer */}
              <div className="rounded-xl border border-dashed border-[hsl(var(--gaze-gold))]/60 bg-[hsl(var(--gaze-ivory))] p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Reply to customer</p>
                <textarea rows={3} value={replyDraft} onChange={(e) => setReplyDraft(e.target.value)} className="mt-2 w-full rounded-lg border border-border px-3 py-2 text-sm" placeholder="Type your reply. Use channel below to log how you sent it." data-testid="inquiry-reply-message" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="text-xs">Channel:</label>
                  <select value={replyChannel} onChange={(e) => setReplyChannel(e.target.value)} className="rounded-lg border border-border bg-white px-2 py-1 text-xs" data-testid="inquiry-reply-channel">
                    <option value="internal">Internal note</option>
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="phone">Phone</option>
                  </select>
                  <button onClick={sendReply} data-testid="inquiry-reply-send" className="ml-auto rounded-full bg-[hsl(var(--primary))] px-4 py-1.5 text-xs font-semibold text-white">Send reply &amp; mark responded</button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium">Update status</label>
                  <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" data-testid="inquiry-status-select">
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="responded">Responded</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Internal note (optional)</label>
                  <textarea rows={2} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" placeholder="e.g. Photos sent via WhatsApp" data-testid="inquiry-note" />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => setSelected(null)} className="rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold">Close</button>
                <button onClick={saveStatus} data-testid="inquiry-save" className="rounded-full bg-[hsl(var(--primary))] px-5 py-2 text-xs font-semibold text-white">Save changes</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
